import { useEffect, useState } from "react";
import Map, { Marker, Popup, NavigationControl } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";

import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import RoomIcon from "@mui/icons-material/Room";
import MenuIcon from "@mui/icons-material/Menu";

export default function SynlabMapLibre() {
    
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [country, setCountry] = useState("All");
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    fetch("/api/locations")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.ok) {
          const withCoords = data.locations.filter((s) => s.lat && s.lng);
          setSites(withCoords);
          setFilteredSites(withCoords);

          // Extract unique countries
          const countryList = Array.from(
            new Set(withCoords.map((s) => s.country || "Unknown"))
          ).sort();
          setCountries(["All", ...countryList]);
        } else {
          setSites([]);
          setFilteredSites([]);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (country === "All") {
      setFilteredSites(sites);
    } else {
      setFilteredSites(sites.filter((s) => s.country === country));
    }
  }, [country, sites]);

  const initialCenter = filteredSites.length
    ? { latitude: filteredSites[0].lat, longitude: filteredSites[0].lng }
    : { latitude: 50.1109, longitude: 8.6821 }; // default Frankfurt

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => setOpenDrawer(true)}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            SYNLAB Locations (MapLibre)
          </Typography>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 150, bgcolor: "white", borderRadius: 1 }}>
            <InputLabel>Country</InputLabel>
            <Select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              label="Country"
            >
              {countries.map((c, i) => (
                <MenuItem key={i} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" sx={{ ml: 2 }}>{filteredSites.length} sites</Typography>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={openDrawer} onClose={() => setOpenDrawer(false)}>
        <Box sx={{ width: 320 }}>
          <Typography variant="h6" sx={{ m: 2 }}>
            SYNLAB Sites
          </Typography>
          <Divider />
          {loading ? (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {filteredSites.map((s, i) => (
                <ListItem
                  key={i}
                  button
                  onClick={() => {
                    setSelected(s);
                    setOpenDrawer(false);
                  }}
                >
                  <RoomIcon sx={{ mr: 1 }} color="error" />
                  <ListItemText primary={s.name} secondary={s.address} />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      <Box sx={{ flex: 1, position: "relative" }}>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Map
            initialViewState={{
              latitude: initialCenter.latitude,
              longitude: initialCenter.longitude,
              zoom: 5
            }}
            style={{ width: "100%", height: "100%" }}
            mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
          >
            <NavigationControl position="top-left" />

            {filteredSites.map((s, i) =>
              s.lat && s.lng ? (
                <Marker
                  key={i}
                  latitude={s.lat}
                  longitude={s.lng}
                  anchor="bottom"
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setSelected(s);
                  }}
                >
                  <RoomIcon color="error" />
                </Marker>
              ) : null
            )}

            {selected && selected.lat && selected.lng && (
              <Popup
                latitude={selected.lat}
                longitude={selected.lng}
                anchor="top"
                onClose={() => setSelected(null)}
                closeOnClick={false}
              >
                <Typography variant="subtitle1">{selected.name}</Typography>
                <Typography variant="body2">{selected.address}</Typography>
                {selected.country && <Typography variant="caption">{selected.country}</Typography>}
              </Popup>
            )}
          </Map>
        )}
      </Box>
    </Box>
  );
}
